import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { resolveOnlineOrderServiceType } from '@constants/orderServiceType';
import type {
  OnlineOrder,
  OnlineOrderStatus,
  OrderStatusEvent,
} from '@src/types/onlineOrder';
import { isActiveOnlineOrderStatus } from '@src/types/onlineOrder';

const COLLECTION = 'online-orders';

function toIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  return new Date().toISOString();
}

function parsePlacedAtCoords(
  raw: unknown
): { latitude: number; longitude: number } | null | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw !== 'object') return undefined;
  const coords = raw as Record<string, unknown>;
  const latitude = Number(coords.latitude);
  const longitude = Number(coords.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
  return { latitude, longitude };
}

function parseOnlineOrder(
  id: string,
  data: Record<string, unknown>
): OnlineOrder | null {
  if (
    typeof data.orderCode !== 'string' ||
    typeof data.restaurantId !== 'string' ||
    typeof data.phone !== 'string' ||
    !Array.isArray(data.cart)
  ) {
    return null;
  }

  const cart = data.cart as OnlineOrder['cart'];
  const createdAt = toIso(data.createdAt ?? data.placedAt);
  const statusHistory = (data.statusHistory as OrderStatusEvent[] | undefined) ?? [];

  return {
    id,
    orderCode: data.orderCode as string,
    orderSeqNo: data.orderSeqNo as string | undefined,
    orderSeqNum: data.orderSeqNum as number | undefined,
    fiscalYear: data.fiscalYear as string | undefined,
    orderSource: 'online-menu',
    serviceType: resolveOnlineOrderServiceType(data.serviceType),
    restaurantId: data.restaurantId as string,
    restaurantName: (data.restaurantName as string) ?? '',
    phone: data.phone as string,
    tableOrRoom: (data.tableOrRoom as string) ?? '',
    instructions: (data.instructions as string) ?? '',
    cart,
    itemCount: (data.itemCount as number) ?? cart.reduce((sum, line) => sum + line.quantity, 0),
    uniqueItemCount: (data.uniqueItemCount as number) ?? cart.length,
    itemsCost: (data.itemsCost as number) ?? 0,
    gstData: (data.gstData as Record<string, number>) ?? {},
    gstBill: (data.gstBill as number) ?? 0,
    serviceBill: (data.serviceBill as number) ?? 0,
    serviceRate: (data.serviceRate as number) ?? 0,
    gstRate: (data.gstRate as number) ?? 0,
    isGstBill: (data.isGstBill as boolean) ?? true,
    totalCost: (data.totalCost as number) ?? 0,
    status: (data.status as OnlineOrderStatus) ?? 'placed',
    statusHistory,
    selectedPayment: typeof data.selectedPayment === 'string' ? data.selectedPayment : 'pending',
    estimatedDeliveryMinutes: data.estimatedDeliveryMinutes as number | undefined,
    estimatedDeliveryAt: data.estimatedDeliveryAt as string | undefined,
    placedAt: (data.placedAt as string) ?? createdAt,
    placedAtCoords: parsePlacedAtCoords(data.placedAtCoords),
    createdAt,
    updatedAt: toIso(data.updatedAt ?? data.placedAt ?? createdAt),
  };
}

function mapOrderDocuments(docs: QueryDocumentSnapshot<DocumentData>[]): OnlineOrder[] {
  return docs
    .map((docSnap) => parseOnlineOrder(docSnap.id, docSnap.data() as Record<string, unknown>))
    .filter((order): order is OnlineOrder => order !== null);
}

export function sortOrdersByPlacedAtDesc(orders: OnlineOrder[]): OnlineOrder[] {
  return [...orders].sort(
    (a, b) => new Date(b.placedAt).getTime() - new Date(a.placedAt).getTime()
  );
}

export function subscribeOnlineOrdersByRestaurant(
  restaurantId: string,
  onUpdate: (orders: OnlineOrder[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  if (!restaurantId) {
    return () => {};
  }

  const db = getFirestore();
  return onSnapshot(
    query(collection(db, COLLECTION), where('restaurantId', '==', restaurantId)),
    (snapshot) => {
      onUpdate(sortOrdersByPlacedAtDesc(mapOrderDocuments(snapshot.docs)));
    },
    (error) => {
      onError?.(error);
    }
  );
}

export interface UpdateOnlineOrderStatusInput {
  orderId: string;
  status: OnlineOrderStatus;
  statusHistory: OrderStatusEvent[];
  estimatedDeliveryMinutes?: number;
  estimatedDeliveryAt?: string;
}

export async function updateOnlineOrderStatus(
  input: UpdateOnlineOrderStatusInput
): Promise<void> {
  const db = getFirestore();
  const now = new Date().toISOString();
  const nextHistory: OrderStatusEvent[] = [
    ...input.statusHistory,
    { status: input.status, at: now },
  ];

  const payload: {
    status: OnlineOrderStatus;
    statusHistory: OrderStatusEvent[];
    updatedAt: ReturnType<typeof serverTimestamp>;
    estimatedDeliveryMinutes?: number;
    estimatedDeliveryAt?: string;
  } = {
    status: input.status,
    statusHistory: nextHistory,
    updatedAt: serverTimestamp(),
  };

  if (input.estimatedDeliveryMinutes !== undefined && input.estimatedDeliveryMinutes !== null) {
    payload.estimatedDeliveryMinutes = input.estimatedDeliveryMinutes;
  }
  if (input.estimatedDeliveryAt) {
    payload.estimatedDeliveryAt = input.estimatedDeliveryAt;
  }

  await updateDoc(doc(db, COLLECTION, input.orderId), payload);
}

export async function updateOnlineOrderDeliveryEta(
  orderId: string,
  estimatedDeliveryMinutes?: number,
  estimatedDeliveryAt?: string
): Promise<void> {
  const db = getFirestore();
  const payload: {
    updatedAt: ReturnType<typeof serverTimestamp>;
    estimatedDeliveryMinutes?: number;
    estimatedDeliveryAt?: string;
  } = {
    updatedAt: serverTimestamp(),
  };

  if (estimatedDeliveryMinutes !== undefined && estimatedDeliveryMinutes !== null) {
    payload.estimatedDeliveryMinutes = estimatedDeliveryMinutes;
  }
  if (estimatedDeliveryAt) {
    payload.estimatedDeliveryAt = estimatedDeliveryAt;
  }

  await updateDoc(doc(db, COLLECTION, orderId), payload);
}

export async function updateOnlineOrderPayment(
  orderId: string,
  selectedPayment: string
): Promise<void> {
  const db = getFirestore();
  await updateDoc(doc(db, COLLECTION, orderId), {
    selectedPayment,
    updatedAt: serverTimestamp(),
  });
}

export function canMergeOnlineOrder(order: OnlineOrder): boolean {
  return isActiveOnlineOrderStatus(order.status);
}

export async function recordOnlineOrderMergedToTable(
  order: OnlineOrder,
  tableNumber: string
): Promise<void> {
  const db = getFirestore();
  const now = new Date().toISOString();
  const orderLabel = order.orderSeqNo ?? order.orderCode;

  await updateDoc(doc(db, COLLECTION, order.id), {
    statusHistory: [
      ...order.statusHistory,
      {
        status: order.status,
        at: now,
        note: `Items merged to ${tableNumber.toUpperCase()} (order ${orderLabel} kept for reference)`,
      },
    ],
    updatedAt: serverTimestamp(),
  });
}

export function buildNewOrderNotification(order: OnlineOrder) {
  const label = order.orderSeqNo ?? order.orderCode;
  return {
    id: `new-order:${order.id}:${order.placedAt}`,
    type: 'new_online_order' as const,
    orderId: order.id,
    orderCode: label,
    restaurantId: order.restaurantId,
    title: 'New online order',
    body: `${label} · ${order.tableOrRoom} · ₹${order.totalCost.toFixed(0)} · ${order.itemCount} items`,
    createdAt: order.placedAt,
    read: false,
  };
}
